import { ChangeEvent } from "react";
import { Button, TextField, Typography } from "@mui/material";
import SearchSelect from "components/atoms/SearchSelect/SearchSelect";
import CepField from "components/atoms/CepField/CepField";
import SelectField from "components/atoms/SelectField/SelectField";
import { TurmaAcaoInsert } from "types/acao";
import { estadosBrasileiros } from "resources/Dados";

import styles_grid from "styles/Grid.module.css";
import styles_general from "styles/General.module.css";
import styles_input from "styles/Input.module.css";
import styles_button from "styles/Button.module.css";

interface TurmaInformacoesFormProps {
  turma: TurmaAcaoInsert;
  errors: Record<string, string>;
  loadingSave: boolean;
  modalidades: any[];
  onInputChange: (value: string | number, field: string, isNumber?: boolean) => void;
  onChangeField: (field: keyof TurmaAcaoInsert, value: any) => void;
  onSave: () => Promise<boolean>;
  onSaveSuccess?: () => void;
  hideSaveButton?: boolean;
}

const TurmaInformacoesForm = ({
  turma,
  errors,
  loadingSave,
  modalidades,
  onInputChange,
  onChangeField,
  onSave,
  onSaveSuccess,
  hideSaveButton = false,
}: TurmaInformacoesFormProps) => {
  const handleCepChange = (data: any) => {
    onChangeField("cep", data.cep || turma.cep || "");
    onChangeField("logradouro", data.logradouro || turma.logradouro || "");
    onChangeField("bairro", data.bairro || turma.bairro || "");
    onChangeField("cidade", data.localidade || turma.cidade || "");
    onChangeField("uf", data.uf || turma.uf || "");
  };

  return (
    <div className={`${styles_general.mt4} ${styles_general.p6} ${styles_grid.grid12}`}>
      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Nome da Turma *</label>
        <TextField
          size="small"
          variant="standard"
          fullWidth
          data-testid="nome"
          value={turma.nome || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "nome")}
          required
          error={!!errors.nome}
          helperText={errors.nome}
          inputProps={{ maxLength: 150 }}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup4}>
        <label className={styles_input.titleInput}>Modalidade *</label>
        <SearchSelect
          label=""
          key="modalidade_id"
          options={modalidades}
          value={turma.modalidade_id}
          onChange={(id) => onChangeField("modalidade_id", id)}
          paperClassname={styles_input.paper}
          popperClassname={styles_input.popper}
          labelClassname={styles_input.labelSelect}
          inputClassname={styles_input.inputSelect}
        />
        {errors.modalidade_id && (
          <Typography color="error" variant="caption" align="left" sx={{ display: "block" }}>
            {errors.modalidade_id}
          </Typography>
        )}
      </div>

      <div className={styles_grid.inputGroup2}>
        <label className={styles_input.titleInput}>Carga Horária</label>
        <TextField
          type="number"
          size="small"
          variant="standard"
          fullWidth
          data-testid="carga_horaria"
          value={turma.carga_horaria ?? ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onChangeField("carga_horaria", e.target.value === "" ? null : Number(e.target.value))
          }
          required
          error={!!errors.carga_horaria}
          helperText={errors.carga_horaria}
          inputProps={{ min: 1 }}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Data de Início das Inscrições *</label>
        <TextField
          type="date"
          size="small"
          variant="standard"
          fullWidth
          data-testid="data_inicio_inscricao"
          value={turma.data_inicio_inscricao || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "data_inicio_inscricao")}
          required
          error={!!errors.data_inicio_inscricao}
          helperText={errors.data_inicio_inscricao}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Data de Término das Inscrições *</label>
        <TextField
          type="date"
          size="small"
          variant="standard"
          fullWidth
          data-testid="data_termino_inscricao"
          value={turma.data_termino_inscricao || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "data_termino_inscricao")}
          required
          error={!!errors.data_termino_inscricao}
          helperText={errors.data_termino_inscricao}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Hora de Início das Inscrições *</label>
        <TextField
          type="time"
          size="small"
          variant="standard"
          fullWidth
          data-testid="hora_inicio_inscricao"
          value={turma.hora_inicio_inscricao || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "hora_inicio_inscricao")}
          required
          error={!!errors.hora_inicio_inscricao}
          helperText={errors.hora_inicio_inscricao}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Hora de Término das Inscrições *</label>
        <TextField
          type="time"
          size="small"
          variant="standard"
          fullWidth
          data-testid="hora_termino_inscricao"
          value={turma.hora_termino_inscricao || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "hora_termino_inscricao")}
          required
          error={!!errors.hora_termino_inscricao}
          helperText={errors.hora_termino_inscricao}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Data de Início da turma *</label>
        <TextField
          type="date"
          size="small"
          variant="standard"
          fullWidth
          data-testid="data_inicio"
          value={turma.data_inicio || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "data_inicio")}
          required
          error={!!errors.data_inicio}
          helperText={errors.data_inicio}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Data de Término da turma *</label>
        <TextField
          type="date"
          size="small"
          variant="standard"
          fullWidth
          data-testid="data_fim"
          value={turma.data_fim || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "data_fim")}
          required
          error={!!errors.data_fim}
          helperText={errors.data_fim}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Hora de Início da turma *</label>
        <TextField
          type="time"
          size="small"
          variant="standard"
          fullWidth
          data-testid="hora_inicio"
          value={turma.hora_inicio || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "hora_inicio")}
          required
          error={!!errors.hora_inicio}
          helperText={errors.hora_inicio}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      <div className={styles_grid.inputGroup6}>
        <label className={styles_input.titleInput}>Hora de Término da turma *</label>
        <TextField
          type="time"
          size="small"
          variant="standard"
          fullWidth
          data-testid="hora_fim"
          value={turma.hora_fim || ""}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "hora_fim")}
          required
          error={!!errors.hora_fim}
          helperText={errors.hora_fim}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      {(turma.modalidade_id == 5 || turma.modalidade_id == 6) && (
        <div className={styles_grid.inputGroupFull}>
          <label className={styles_input.titleInput}>Link</label>
          <TextField
            size="small"
            variant="standard"
            fullWidth
            data-testid="link"
            value={turma.link}
            error={!!errors.link}
            helperText={errors.link}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "link")}
            inputProps={{ maxLength: 150 }}
            InputLabelProps={{ shrink: true }}
            InputProps={{ className: styles_input.input, disableUnderline: true }}
          />
        </div>
      )}

      {turma.modalidade_id == 4 && (
        <>
          <div className={styles_grid.inputGroup3}>
            <label className={styles_input.titleInput}>CEP *</label>
            <CepField
              value={turma.cep || ""}
              onCepChange={handleCepChange}
              customClassName={styles_input.input}
              error={!!errors.cep}
              helperText={errors.cep}
            />
          </div>

          <div className={styles_grid.inputGroup6}>
            <label className={styles_input.titleInput}>Logradouro *</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={turma.logradouro || ""}
              error={!!errors.logradouro}
              helperText={errors.logradouro}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "logradouro")}
              inputProps={{ maxLength: 250 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{ className: styles_input.input, disableUnderline: true }}
            />
          </div>

          <div className={styles_grid.inputGroup3}>
            <label className={styles_input.titleInput}>Número *</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={turma.numero || ""}
              error={!!errors.numero}
              helperText={errors.numero}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onInputChange(e.target.value.replace(/\D/g, ""), "numero")
              }
              inputProps={{ maxLength: 20 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{ className: styles_input.input, disableUnderline: true }}
            />
          </div>

          <div className={styles_grid.inputGroup3}>
            <label className={styles_input.titleInput}>Complemento</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={turma.complemento || ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "complemento")}
              inputProps={{ maxLength: 250 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{ className: styles_input.input, disableUnderline: true }}
            />
          </div>

          <div className={styles_grid.inputGroup3}>
            <label className={styles_input.titleInput}>Bairro *</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={turma.bairro || ""}
              error={!!errors.bairro}
              helperText={errors.bairro}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "bairro")}
              inputProps={{ maxLength: 250 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{ className: styles_input.input, disableUnderline: true }}
            />
          </div>

          <div className={styles_grid.inputGroup4}>
            <label className={styles_input.titleInput}>Cidade *</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={turma.cidade || ""}
              error={!!errors.cidade}
              helperText={errors.cidade}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "cidade")}
              InputLabelProps={{ shrink: true }}
              InputProps={{ className: styles_input.input, disableUnderline: true }}
            />
          </div>

          <div className={styles_grid.inputGroup2}>
            <label className={styles_input.titleInput}>UF *</label>
            <SelectField
              label=""
              value={turma.uf || ""}
              error={!!errors.uf}
              helperText={errors.uf}
              onChange={(value) => onChangeField("uf", value)}
              options={estadosBrasileiros.map((estado) => ({ nome: estado.sigla }))}
              disableTyping
            />
          </div>

          

          

          
        </>
      )}

      <div className={styles_grid.inputGroupFull}>
        <label className={styles_input.titleInput}>Observacoes</label>
        <TextField
          size="small"
          variant="standard"
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          data-testid="observacoes"
          value={turma.observacoes}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onInputChange(e.target.value, "observacoes")}
          inputProps={{ maxLength: 150 }}
          InputLabelProps={{ shrink: true }}
          InputProps={{ className: styles_input.input, disableUnderline: true }}
        />
      </div>

      {!hideSaveButton && (
        <div className={`${styles_grid.inputGroupFull} ${styles_grid.justifyCenter} ${styles_general.mt2}`}>
          <Button
            variant="contained"
            onClick={async () => {
              const success = await onSave();
              if (success) {
                onSaveSuccess?.();
              }
            }}
            className={`${styles_button.buttonPrimary} ${loadingSave ? styles_button.buttonLoad : ''} `}
            color="primary"
          >
            {loadingSave ? (
              <>
                <div className={styles_button.spinner}></div>
                <span>Salvando...</span>
              </>
            ) : (
              <span>Salvar Informações da Turma</span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TurmaInformacoesForm;


